import React from 'react';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowBranchFlow from './components/WorkflowBranchFlow';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
    active: true,
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  { label: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const WorkflowBeStep1: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part1 — スキーマ定義とAPI仕様書を作成する"
      seriesName="バックエンド開発編"
      tags={['Backend', 'Schema', 'API Spec']}
      seriesSteps={beSteps}
      prevLink={{ label: '要件定義編 Part4: usecases出力', path: '/devdocs/workflow/req-step4' }}
      nextLink={{
        label: 'Part2: API実装ワークフローとその全体像',
        path: '/devdocs/workflow/be-step2',
      }}
    >
      <WorkflowLearningPoints
        items={[
          'usecases.md からスキーマ定義書とAPI仕様書を作成する手順',
          'スキーマ定義書・API仕様書のフォーマットと命名規則',
          'Flywayマイグレーション・権限一覧・API一覧の連動更新',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: 'スキーマ定義' },
          { id: 'section-3', label: 'API仕様書' },
          { id: 'section-4', label: 'Flywayマイグレーションの作成' },
          { id: 'section-5', label: '実行手順' },
          { id: 'section-6', label: '注意すべきこと' },
          { id: 'section-7', label: '成果物まとめ' },
          { id: 'section-8', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-9', label: 'この先の流れ' },
        ]}
      />

      {/* セクション1: 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowText>
          要件定義フェーズで作成した *.usecases.md と *.proposal.md
          から、基本設計の公式ドキュメントを作成します。
        </WorkflowText>
        <WorkflowBranchFlow
          input={{ label: 'usecases.md / proposal.md', highlight: true }}
          outputs={[
            { label: 'スキーマ定義（テーブル定義書）', sub: '配置先: docs/20_BD/10_スキーマ定義/' },
            { label: 'API仕様書', sub: '配置先: docs/20_BD/30_API仕様書/' },
          ]}
        />
        <WorkflowInfoBox type="info">
          基本的にAIが生成します。proposal.md と usecases.md
          を入力にしてスキーマ定義・API仕様書を生成します。人間が手を加えるのは「既存サービスを利用させたい」等の意図がある場合に処理概要を追記する程度です。I/F（Request/Response）を修正したい場合は
          proposal.md
          から修正するのが正しい手順であり、生成後のAPI仕様書を直接書き換えることはほぼありません。
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション2: スキーマ定義 */}
      <WorkflowSection title="スキーマ定義" id="section-2" number={2}>
        <WorkflowSection title="配置先・命名規則" subNumber="2.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', 'docs/20_BD/10_スキーマ定義/'],
              ['命名規則', '{メジャーバージョン}_{連番}__{テーブル物理名}.md'],
              ['例', '02_002__partner.md'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="スキーマ定義書のフォーマット" subNumber="2.2">
          <WorkflowCodeBlock
            title="テンプレート全体を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# テーブル定義

## テーブル名

| 論理名 | 物理名 |
|--------|--------|
| パートナー | partner |

## 概要

パートナー企業の情報を管理する。

## テーブル定義

| 論理名 | 物理名 | 種別 | 閾値 | Not Null | PK | 備考 |
|--------|--------|------|------|----------|----|------|
| パートナーID | partner_id | varchar(64) | | 〇 | 〇 | UUID |
| パートナー名 | partner_name | varchar(300) | | 〇 | | |
| ... | ... | ... | | ... | | ... |

## unique key

なし

## index

| index名 | column | seq |
|---------|--------|-----|
| partner_name_kana_idx | partner_name_kana | 1 |

## foreign key

なし`}
          />
        </WorkflowSection>

        <WorkflowSection title="スキーマ定義作成ルール: create_schema.md の紹介" subNumber="2.3">
          <WorkflowText>
            スキーマ定義書を作成する際にAIが自動的に参照するルールファイルです。Front Matterは
            fileMatch で docs/20_BD/10_スキーマ定義/**/*.md を指定します。
          </WorkflowText>
          <WorkflowText>
            テンプレート構造だけでなく、AIが守るべき記載ルールもSteeringに定義しておきます。
          </WorkflowText>
          <WorkflowCodeBlock
            title="create_schema.md"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# スキーマ定義ルール

## ファイル命名規則

- ファイル名: \`{メジャーバージョン}_{連番}__{テーブル物理名}.md\`
  - メジャーバージョン: Flywayマイグレーションのメジャーバージョンに対応する2桁の数字
  - 連番: そのメジャーバージョン内での通し番号を3桁で付与する
  - テーブル物理名: テーブルの物理名をそのまま使用する

## ドキュメント構造

テーブル定義書は以下の構造で記述する。

### 必須セクション

1. \`# テーブル定義\` — ドキュメントタイトル（固定）
2. \`## テーブル名\` — 論理名と物理名のテーブル
3. \`## 概要\` — テーブルの説明（1〜2文で簡潔に記述する）
4. \`## テーブル定義\` — カラム定義テーブル
5. \`## unique key\` — ユニークキー定義（なければ「なし」と記載）
6. \`## index\` — インデックス定義（なければ空テーブルのヘッダのみ記載）

### 任意セクション

- \`## foreign key\` — 外部キー定義（なければ「なし」と記載、または省略可）
- \`## code\` — コードテーブルの場合にマスターデータを記載する

## カラム定義テーブルの記述ルール

| 項目     | 記述ルール                                            |
|----------|------------------------------------------------------|
| 論理名   | 用語集に準拠する                                      |
| 物理名   | スネークケース（小文字）で記述する                     |
| 種別     | MySQL 8.0 のデータ型を記載する                        |
| Not Null | NOT NULL 制約がある場合は \`〇\` を記載する             |
| PK       | 主キーの場合は \`〇\` を記載する                        |

### 種別（データ型）のルール

- 文字列: \`varchar(n)\` — nは最大文字数
- 日時: \`datetime\`
- 整数: \`int\`, \`bigint\`
- 真偽値: \`tinyint(1)\`
- 小数: \`decimal(m,n)\`

## unique key / index のルール

- ユニークキー名のサフィックスは \`_uk\` とする
- index名のサフィックスは \`_idx\` とする
- 複合indexの場合は同じindex名で \`seq\` を連番にする

## 用語集との整合性

- 論理名・物理名は用語集と整合を取る
- 用語集に存在しない用語を使用する場合は用語集にも追記する
- 用語集と物理名が異なる場合は、用語集を正とする

## Flywayマイグレーションとの連携

スキーマ定義書を新規作成・修正した場合は、対応するFlywayマイグレーションSQLファイルの作成・修正も行う。`}
          />

          <WorkflowInfoBox type="warning">
            このフォーマットはプロジェクトによって変わります。上記は本プロジェクト（MySQL
            8.0）向けのフォーマットです。技術スタックが異なれば変わります：
            {'\n\n'}
            ・PostgreSQL の場合 — データ型が serial, text, timestamptz 等になる
            {'\n'}
            ・NoSQL の場合 — テーブル定義ではなくコレクション設計・アクセスパターンになる
            {'\n\n'}
            自分のプロジェクトに合わせてSteeringのフォーマットをカスタマイズしてください。
          </WorkflowInfoBox>

          <WorkflowInfoBox type="info">
            本プロジェクトで実際に生成されたスキーマ定義書のサンプルはバックエンド開発編 付録 —
            成果物サンプル集に掲載しています。
          </WorkflowInfoBox>

          <WorkflowTipBox title="用語集の役割と更新タイミング">
            本プロジェクトにおける用語集は「論理名 ↔
            物理名」の対応表です。用語の意味を説明するドキュメントではありません。
            {'\n\n'}
            物理名が登場するのは基本設計（このステップ）からなので、用語集が必要になるのもこのタイミングが最初です。要件定義フェーズでは不要でした。
            {'\n\n'}
            用語集への追記は手動で管理するのではなく、api_project_rule
            ワークフロー（Part2〜4）の中で「使った物理名が用語集にない →
            追記する」ステップとして組み込まれています。スキーマ定義やAPI仕様書の生成時点では、既存の用語集を参照して整合を取るだけで十分です。
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3: API仕様書 */}
      <WorkflowSection title="API仕様書" id="section-3" number={3}>
        <WorkflowSection title="配置先・命名規則" subNumber="3.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', 'docs/20_BD/30_API仕様書/'],
              ['命名規則', 'API{ID}_{API名}.md'],
              ['例', 'API3001_パートナー登録API.md'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="API仕様書のフォーマット" subNumber="3.2">
          <WorkflowCodeBlock
            title="テンプレート全体を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API{ID} {API名}

## 変更履歴

| version | 変更内容 | diff |
|---------|----------|------|
| v1 | 新規作成 | [diff](../../.kiro/specs/10_API/API{ID}/v1/diff.md) |

## 概要

| API ID | API名 | API概要 | Method | Endpoint |
|--------|-------|---------|--------|----------|
| API{ID} | {API名} | {概要} | POST | /v1/{resource}/{action} |

## 権限

| 権限 |
|------|
| ADMIN |
| BACK_OFFICE |
| MANAGER |

## Request

### Body

| 論理名 | 物理名 | type | NotNull | max | format | 備考 |
|--------|--------|------|---------|-----|--------|------|
| ... | ... | string | 〇 | 300 | | ... |

## Response

### Status Code

| Error | Code | 備考 |
|-------|------|------|
| バリデーションエラー | 400 | |
| 認証エラー | 401 | セッションが存在しない |
| 認可エラー | 403 | 権限が足りない |

### Body

| 論理名 | 物理名 | type | format | NotNull | 備考 |
|--------|--------|------|--------|---------|------|
| ... | ... | string | | 〇 | ... |

## 参照DB

- [テーブル名](../10_スキーマ定義/XX_XXX__table.md)

## 処理概要

1. {処理1}
2. {処理2}
3. ...`}
          />
        </WorkflowSection>

        <WorkflowSection title="API仕様書作成ルール: api_specs_rule.md の紹介" subNumber="3.3">
          <WorkflowText>
            API仕様書を作成・修正する際にAIが自動的に参照するルールファイルです。Front Matterは
            fileMatch で docs/20_BD/20_API一覧/**/*.md と docs/20_BD/30_API仕様書/**/*.md
            を指定します。
          </WorkflowText>
          <WorkflowCodeBlock
            title="api_specs_rule.md"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# API仕様書ルール

## AIが行う作業

- 参照DBに過不足があればスキーマ定義へのリンクの追加/削除を行う。
- 人が初期作成した内容を元に必要なController, Usecase, Model, Service, Repositoryを生成する。
    - 生成したファイルは docs/30_DD/ 配下に作成する。
- APIには Controller と UseCase が1個紐づく。
    - 作成した Controller と UseCase のリンクを当該API仕様書に記載すること。
- API仕様書を修正した場合は必ず API一覧.md の修正が必要かを確認すること。
    - 修正の必要があれば修正する。

## 権限制御の連動ルール

API仕様書の「権限」セクションを追加・変更・削除した場合は、以下を必ず連動して更新する。

### 1. 権限一覧の更新

- 権限一覧.md の該当行を追加/修正/削除する。
- 権限一覧の「必要権限」列はAPI仕様書の「権限」セクションと完全に一致させる。
- 権限が不要（ログイン済み全ユーザーアクセス可）の場合は「なし」と記載する。

### 2. マイグレーションファイルの作成

- migration_rule.md の「権限マイグレーション」に従い、Flywayの新しいバージョンのマイグレーションファイルを作成する。

### 3. 整合性チェック

- 全マイグレーションファイルを順に適用した結果のDB状態が権限一覧と一致することを確認する。

## Request/Response記載ルール

- オブジェクトがネストする場合は type は object または object[] とする。
- オブジェクトがネストする場合は &emsp; によってインデントをつける。
- Responseのlist型についてはnullは返却しない。0件の場合でも空のlistを返却する仕様とする。
- 日付・日時の項目は type を \`datetime\` とし、format に \`yyyy-MM-dd'T'HH:mm:ss\` を記載する。
- パスパラメータがある場合は Endpoint に \`{パラメータ名}\` を含め、Request内に \`### パスパラメータ\` セクションを追加する。
    - パスパラメータのテーブルは 論理名, 物理名, type, NotNull, 備考 の5列で記載する。
- 1件取得APIのResponseは \`result\` (object) でネストする。
- 複数件取得APIのResponseは \`results\` (object[]) でネストする。

# API一覧ルール

API仕様書から以下のカラムをAPI一覧に転記する。各カラムは対応するDD書へのリンクを付与する。

## API ID

- API仕様書のAPI IDとリンク

## API名

- API仕様書のAPI名とリンク

## Controller

- docs/30_DD/50_controller/ 配下の当該クラス名とリンク
- 利用するControllerの数だけ行を記載する

## UseCase

- docs/30_DD/20_usecase/ 配下の当該クラス名とリンク
- 利用するUsecaseの数だけ行を記載する

## Model

- docs/30_DD/10_model/ 配下の当該クラス名とリンク
- 利用するModelの数だけ行を記載する

## Service

- docs/30_DD/30_service/ 配下の当該クラス名とリンク
- 利用するServiceの数だけ行を記載する

## Repository

- docs/30_DD/40_repository/ 配下の当該クラス名とリンク
- 利用するRepositoryの数だけ行を記載する`}
          />

          <WorkflowInfoBox type="warning">
            このフォーマットはプロジェクトによって変わります。上記は REST
            API（JSON）向けのフォーマットです。技術スタックが異なれば変わります：
            {'\n\n'}
            ・GraphQL の場合 — Request/Responseテーブルではなく Schema 定義になる
            {'\n'}
            ・gRPC の場合 — .proto ファイルの定義が中心になる
            {'\n\n'}
            自分のプロジェクトに合わせてSteeringのフォーマットをカスタマイズしてください。
          </WorkflowInfoBox>

          <WorkflowInfoBox type="info">
            本プロジェクトで実際に生成されたAPI仕様書のサンプルはバックエンド開発編 付録 —
            成果物サンプル集に掲載しています。
          </WorkflowInfoBox>

          <WorkflowTipBox title="なぜAPI一覧・権限一覧のルールをAPI仕様書ルールに含めているのか">
            API一覧や権限一覧の更新は「API仕様書を作成・修正したタイミング」で連動して行う作業です。fileMatchのglobが同じ（=
            発火タイミングが同じ）なので、Steeringファイルを分ける必要がありません。
            {'\n\n'}
            Steeringファイルを分けるかどうかの判断基準はシンプルです:
            {'\n\n'}
            ・分ける → 発火タイミング（glob）が異なる / ルールが大きく独立性が高い /
            単独で参照・変更したいケースがある
            {'\n'}
            ・まとめる → 発火タイミングが同じ / ルールが軽く親ルールに連動する /
            常に親ルールと一緒に参照される
            {'\n\n'}
            API一覧・権限一覧は「転記ルール」程度の軽い内容で、API仕様書を触らずにこれらだけ更新するケースはほぼ存在しません。1ファイルにまとめることでAIが一度に全部やってくれます。
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: Flywayマイグレーションの作成 */}
      <WorkflowSection title="Flywayマイグレーションの作成" id="section-4" number={4}>
        <WorkflowText>
          スキーマ定義書を作成したら、対応するFlywayマイグレーションSQLも作成します。
        </WorkflowText>

        <WorkflowSection title="マイグレーションファイルの配置" subNumber="4.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', 'src/main/resources/db/migration/'],
              ['命名規則', 'V{メジャー}_{マイナー}__{説明}.sql'],
              ['例', 'V2_1__create_partner.sql'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection
          title="Flywayマイグレーション作成ルール: migration_rule.md の紹介"
          subNumber="4.2"
        >
          <WorkflowText>
            マイグレーションSQLの記述ルールをSteeringに定義しておきます。Front Matterは fileMatch で
            src/main/resources/db/migration/**/*.sql を指定します。
          </WorkflowText>
          <WorkflowText>
            以下は一般的なSQLルールの部分です。プロジェクト固有のルール（権限テーブルへのINSERT等）があれば追記してください。
          </WorkflowText>
          <WorkflowCodeBlock
            title="migration_rule.md"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# Flywayマイグレーションルール

## マイグレーションファイルの命名規則

- \`V{メジャー}_{マイナー}__{説明}.sql\`
  - メジャー: 機能グループの番号
  - マイナー: そのメジャーバージョン内での連番
  - 説明: 操作内容をスネークケースで記述する
- 初期データ投入は \`V{メジャー}_90__init_data.sql\` とする

## SQLの記述ルール

### テーブル作成時

- \`ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_general_ci\` を末尾に付与する
- PRIMARY KEY はCREATE TABLE文内で定義する
- UNIQUE KEY はCREATE TABLE文内で定義する
- INDEX はCREATE TABLE文内で定義する

### テーブル変更時

- カラム追加/変更/削除時: \`ALTER TABLE\` を使用する
- INDEX追加時: \`CREATE INDEX\` を使用する
- INDEX削除時: \`ALTER TABLE ... DROP INDEX\` を使用する

### データ操作時

- 初期データ投入時: 1行ずつ \`INSERT INTO\` 文を記載する`}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: 実行手順 */}
      <WorkflowSection title="実行手順" id="section-5" number={5}>
        <WorkflowSection title="AIへの指示例" subNumber="5.1">
          <WorkflowChatBubble role="human">
            partner.usecases.md と partner.proposal.md を参照して、{'\n'}
            以下を作成してください。{'\n\n'}
            1. スキーマ定義書（docs/20_BD/10_スキーマ定義/ に配置）{'\n'}
            {'   '}- master_typeテーブル（partner_type区分の追加）{'\n'}
            {'   '}- partnerテーブル{'\n\n'}
            2. API仕様書（docs/20_BD/30_API仕様書/ に配置）{'\n'}
            {'   '}- API3001 パートナー登録API{'\n'}
            {'   '}- API3002 パートナー更新API{'\n'}
            {'   '}- API3004 パートナー取得API{'\n'}
            {'   '}- API3005 パートナー一覧取得API
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            Flywayマイグレーション・権限一覧・API一覧・権限マイグレーションは各Steeringルール（create_schema.md、api_specs_rule.md）で連動して自動更新されるため、プロンプトへの指示は不要です。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-6" number={6}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              'I/F修正はproposalから',
              'Request/Responseを修正したい場合は生成後のAPI仕様書を直接書き換えず、proposal.md を修正して再生成する',
            ],
            [
              '修正時の影響波及',
              'スキーマ定義やAPI仕様書を手修正した場合、その変更を proposal.md → business.md → usecases.md にも反映しないと画面実装時に不整合が起きる',
            ],
            [
              '用語集との整合性',
              '物理名は必ず用語集.mdと一致させる。一致しない場合は用語集を正とする',
            ],
            [
              '権限の連動更新',
              'Steeringルールで自動連動するが、生成結果を確認して権限一覧とマイグレーションの整合性が取れていることを目視確認する',
            ],
            [
              'API一覧の連動更新',
              'Steeringルールで自動連動するが、生成結果を確認してAPI一覧に正しく転記されていることを目視確認する',
            ],
            [
              '処理概要まで書く',
              'API仕様書の処理概要まで記載する。ここが後続のDD書のインプットになる',
            ],
          ]}
        />
      </WorkflowSection>

      {/* セクション7: 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-7" number={7}>
        <WorkflowTable
          headers={['成果物', '配置先', '利用先']}
          rows={[
            ['スキーマ定義書', 'docs/20_BD/10_スキーマ定義/', 'DD書作成、マイグレーション生成'],
            ['API仕様書', 'docs/20_BD/30_API仕様書/', 'DD書作成、コード生成、テスト'],
            ['Flywayマイグレーション', 'src/main/resources/db/migration/', 'DB構築'],
            ['権限一覧', 'docs/20_BD/40_権限/権限一覧.md', '認可制御の管理'],
            ['API一覧', 'docs/20_BD/20_API一覧/API一覧.md', '全体の俯瞰、リンク管理'],
          ]}
        />
      </WorkflowSection>

      {/* セクション8: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-8" number={8}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'proposal.md を Single Source of Truth にする',
              effect:
                'I/F変更時に proposal → スキーマ定義 → API仕様書の順で自動再生成できる。逆方向の手修正は整合性が崩れる',
            },
            {
              practice: 'スキーマ定義を先に確定させる',
              effect:
                'テーブル構造が決まればAPI仕様書のRequest/Responseは機械的に導出できる。逆順だとカラム設計が歪む',
            },
            {
              practice: '処理概要を具体的に書く',
              effect:
                'DD書のインプットになるため、曖昧な記述は後工程の品質に直結する。判定条件・例外ケース・副作用を明示する',
            },
            {
              practice: 'Steeringのglobパターンを適切に設定する',
              effect:
                'fileMatch を正しく設定しておけば、ファイル作成時にルールが自動適用される。手動で渡す必要がなくなる',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'API仕様書を直接手書きする',
              effect:
                'proposalとの乖離が生じ、画面実装時にI/F不整合が発覚する。proposalを修正して再生成するのが正しいアプローチ',
            },
            {
              practice: 'スキーマ定義なしにAPI仕様書を書く',
              effect:
                '型やカラム名が曖昧なままI/Fが決まり、後からテーブル修正が発生する。スキーマ定義→API仕様書の順で作成する',
            },
            {
              practice: 'Flywayマイグレーションを手動管理する',
              effect:
                'Steeringの連動ルールと二重管理になり、整合性チェックが抜ける。スキーマ定義を修正すればマイグレーションは自動連動に任せる',
            },
            {
              practice: '権限セクションの更新後に権限一覧を放置する',
              effect:
                '権限一覧とDBの不一致が蓄積し、認可バグが埋もれる。Steeringの連動更新を信頼しつつ、生成結果を目視確認する',
            },
            {
              practice: '用語集と異なる物理名をそのまま使う',
              effect:
                'AIが次回以降のコード生成で古い物理名と新しい物理名を混在させる。用語集を正とし、常に整合を取る',
            },
            {
              practice: '処理概要を空欄にする',
              effect:
                'DD書の処理セクションが曖昧になり、Phase2で誤ったコードが生成される。主要な処理ステップ・条件分岐・エラーケースを漏れなく記載する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション9: この先の流れ */}
      <WorkflowSection title="この先の流れ" id="section-9" number={9}>
        <WorkflowText>
          このステップが完了すると、api_project_rule.md ワークフローに入ります。
        </WorkflowText>
        <WorkflowProcedureFlow
          steps={[
            { label: 'Part1: スキーマ定義 + API仕様書', variant: 'complete' },
            { label: 'Part2: api_project_rule Phase1 — 設計書の作成（diff/instructions/DD書）' },
            { label: 'Part3: api_project_rule Phase2 — コード生成' },
            { label: 'Part4: api_project_rule Phase3 — 単体テスト' },
            { label: 'Part5: E2Eテスト — 機能横断シナリオの検証' },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep1;
